import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PitBoardLogo from "@/components/PitBoardLogo";
import { Download, Lock, ShieldAlert } from "lucide-react";

// Tous les fichiers téléchargeables (SVG + PNG multi-résolutions).
const LOGOS = [
  {
    key: "color",
    label: "Logo couleur",
    desc: "Version officielle bicolore — jaune & rouge. Format principal.",
    bg: "var(--carbon)",
    preview: "/brand/pitboard-logo.svg",
    files: [
      { name: "SVG vectoriel",   path: "/brand/pitboard-logo.svg",         badge: "RECOMMANDÉ" },
      { name: "PNG 512px",       path: "/brand/pitboard-logo-512.png" },
      { name: "PNG 1024px",      path: "/brand/pitboard-logo-1024.png" },
      { name: "PNG 2048px (HD)", path: "/brand/pitboard-logo-2048.png",    badge: "HD" },
    ],
  },
  {
    key: "carbon",
    label: "Logo sur fond carbone",
    desc: "Version couleur sur fond noir #0B0B0C — idéal pour signatures email, documents bureautiques.",
    bg: "#0B0B0C",
    preview: "/brand/pitboard-logo-carbon.svg",
    files: [
      { name: "SVG vectoriel",  path: "/brand/pitboard-logo-carbon.svg" },
      { name: "PNG 512px",      path: "/brand/pitboard-logo-carbon-512.png" },
      { name: "PNG 1024px",     path: "/brand/pitboard-logo-carbon-1024.png" },
    ],
  },
  {
    key: "white",
    label: "Logo monochrome blanc",
    desc: "À utiliser sur fond foncé, rouge ou photo sombre. Watermark vidéo, t-shirt, packaging.",
    bg: "#0B0B0C",
    preview: "/brand/pitboard-logo-white.svg",
    files: [
      { name: "SVG vectoriel",   path: "/brand/pitboard-logo-white.svg" },
      { name: "PNG 512px",       path: "/brand/pitboard-logo-white-512.png" },
      { name: "PNG 1024px",      path: "/brand/pitboard-logo-white-1024.png" },
      { name: "PNG 2048px (HD)", path: "/brand/pitboard-logo-white-2048.png" },
    ],
  },
  {
    key: "black",
    label: "Logo monochrome noir",
    desc: "Impression noir et blanc, fond clair, watermark sur photo claire.",
    bg: "#FFFFFF",
    preview: "/brand/pitboard-logo-black.svg",
    files: [
      { name: "SVG vectoriel",   path: "/brand/pitboard-logo-black.svg" },
      { name: "PNG 512px",       path: "/brand/pitboard-logo-black-512.png" },
      { name: "PNG 1024px",      path: "/brand/pitboard-logo-black-1024.png" },
      { name: "PNG 2048px (HD)", path: "/brand/pitboard-logo-black-2048.png" },
    ],
  },
  {
    key: "favicon",
    label: "Favicon (icône carrée)",
    desc: "Pour onglet navigateur, PWA, raccourcis bureau.",
    bg: "var(--carbon)",
    preview: "/favicon.svg",
    files: [
      { name: "SVG vectoriel", path: "/favicon.svg" },
      { name: "PNG 192px",     path: "/brand/favicon-192.png" },
      { name: "PNG 512px",     path: "/brand/favicon-512.png" },
    ],
  },
];

const COLORS = [
  { name: "Jaune Pit",   hex: "#FFC400", role: "Accent principal — premier mot, highlights, succès." },
  { name: "Rouge Board", hex: "#E10600", role: "CTA principal — second mot, alertes, énergie." },
  { name: "Carbone",     hex: "#0B0B0C", role: "Fond principal — élégance, contraste maximal." },
  { name: "Steel",       hex: "#2A2A2D", role: "Bordures, séparateurs." },
  { name: "Chalk",       hex: "#F4F4F2", role: "Texte sur fond carbone." },
];

export default function BrandAssets() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="text-secondary-text flex items-center gap-3"><div className="spinner" /><span>Chargement…</span></div>
      </div>
    );
  }
  // Si non connecté → redirige vers /login
  if (!user) return <Navigate to="/login" replace />;
  // Si connecté mais pas platform admin → 403
  if (!user.is_platform_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app p-6">
        <div className="card p-8 max-w-md text-center">
          <ShieldAlert size={36} className="mx-auto mb-4" style={{ color: "var(--board-red)" }} />
          <h1 className="font-display text-2xl mb-2" style={{ fontWeight: 700 }}>Accès restreint</h1>
          <p className="text-secondary-text text-sm mb-5">
            Cette page est réservée à l'éditeur de PitBoard (MJSA INVEST). Elle contient les éléments d'identité de marque non destinés aux abonnés.
          </p>
          <Link to="/" className="btn btn-outline">← Retour à mon espace</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app text-[var(--chalk)]">
      <nav className="border-b border-[var(--steel)] sticky top-0 z-40 backdrop-blur bg-[rgba(11,11,12,0.85)]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><PitBoardLogo size={36} /></Link>
          <Link to="/" className="btn btn-outline btn-sm">← Retour à mon espace</Link>
        </div>
      </nav>

      <article className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={14} style={{ color: "var(--pit-yellow)" }} />
          <p className="text-xs font-semibold" style={{ color: "var(--pit-yellow)" }}>BRAND ASSETS — RÉSERVÉ MJSA INVEST</p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-3 leading-[1.05]">Logos & charte graphique</h1>
        <p className="text-[15px] max-w-2xl mb-12" style={{ color: "var(--ink-soft)" }}>
          Téléchargez les fichiers officiels PitBoard dans tous les formats utiles (SVG vectoriel + PNG haute résolution).
          Page accessible uniquement aux administrateurs plateforme.
        </p>

        <h2 className="font-display text-2xl mb-5" style={{ fontWeight: 700, color: "var(--pit-yellow)" }}>Logos</h2>
        <div className="space-y-6 mb-14">
          {LOGOS.map((logo) => (
            <div key={logo.key} className="rounded-xl border overflow-hidden grid sm:grid-cols-[1fr_2fr]" style={{ borderColor: "var(--steel)", background: "var(--carbon-2)" }} data-testid={`brand-card-${logo.key}`}>
              <div className="h-44 sm:h-auto flex items-center justify-center p-8" style={{ background: logo.bg }}>
                <img src={logo.preview} alt="" style={{ maxHeight: 80, maxWidth: "90%" }} />
              </div>
              <div className="p-5">
                <div className="font-semibold mb-1">{logo.label}</div>
                <div className="text-sm mb-4" style={{ color: "var(--ink-soft)" }}>{logo.desc}</div>
                <div className="grid grid-cols-2 gap-2">
                  {logo.files.map((f) => (
                    <a
                      key={f.path}
                      href={f.path}
                      download
                      className="btn btn-outline btn-sm justify-start"
                      data-testid={`dl-${logo.key}-${f.path.split("/").pop()}`}
                    >
                      <Download size={13} />
                      <span className="flex-1 text-left text-xs">{f.name}</span>
                      {f.badge && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold" style={{ background: "rgba(255,196,0,0.15)", color: "var(--pit-yellow)" }}>
                          {f.badge}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <h2 className="font-display text-2xl mb-5" style={{ fontWeight: 700, color: "var(--pit-yellow)" }}>Couleurs officielles</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
          {COLORS.map((c) => (
            <div key={c.hex} className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--steel)", background: "var(--carbon-2)" }}>
              <div className="h-24" style={{ background: c.hex }} />
              <div className="p-4">
                <div className="font-semibold">{c.name}</div>
                <div className="font-mono text-sm" style={{ color: "var(--pit-yellow)" }}>{c.hex}</div>
                <div className="text-xs mt-1.5" style={{ color: "var(--ink-soft)" }}>{c.role}</div>
              </div>
            </div>
          ))}
        </div>

        <h2 className="font-display text-2xl mb-5" style={{ fontWeight: 700, color: "var(--pit-yellow)" }}>Typographie</h2>
        <div className="grid sm:grid-cols-2 gap-5 mb-14">
          <div className="rounded-lg border p-6" style={{ borderColor: "var(--steel)", background: "var(--carbon-2)" }}>
            <div className="text-xs font-semibold mb-3" style={{ color: "var(--grey)" }}>POLICE D'AFFICHAGE</div>
            <div className="font-display text-5xl mb-2" style={{ fontWeight: 800 }}>Fraunces</div>
            <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Serif moderne — titres, chiffres, logo (Black 900).</div>
            <a href="https://fonts.google.com/specimen/Fraunces" target="_blank" rel="noopener noreferrer" className="text-sm underline mt-2 inline-block" style={{ color: "var(--pit-yellow)" }}>Google Fonts ↗</a>
          </div>
          <div className="rounded-lg border p-6" style={{ borderColor: "var(--steel)", background: "var(--carbon-2)" }}>
            <div className="text-xs font-semibold mb-3" style={{ color: "var(--grey)" }}>POLICE DE TEXTE</div>
            <div className="text-3xl mb-2" style={{ fontWeight: 700, fontFamily: "Inter, sans-serif" }}>Inter</div>
            <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Sans-serif neutre — corps de texte, interface, chiffres compacts.</div>
            <a href="https://fonts.google.com/specimen/Inter" target="_blank" rel="noopener noreferrer" className="text-sm underline mt-2 inline-block" style={{ color: "var(--pit-yellow)" }}>Google Fonts ↗</a>
          </div>
        </div>

        <p className="text-xs" style={{ color: "var(--grey)" }}>
          © 2026 PitBoard · Édité par MJSA INVEST. Les logos PitBoard sont une marque déposée — leur utilisation est restreinte aux partenaires officiels.
        </p>
      </article>
    </div>
  );
}
